import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../core/auth.service';

@Component({
  selector: 'app-login',
  imports: [FormsModule],
  template: `
    <section class="vm-login">
      <div class="vm-login__logo-lockup">
        <img src="assets/images/logo.png" alt="Visual Matrix">
      </div>

      <form class="vm-login__form" (ngSubmit)="submit()">
        <h1>Sign In</h1>
        <p class="vm-login__product">Visual Matrix Importer</p>

        @if (auth.authenticationMode() !== 'local') {
          <p class="vm-login__copy">Use your approved Zoho account to access this application.</p>
          <button class="vm-login__submit" type="button" (click)="beginZohoLogin()" [disabled]="busy()">
            {{ busy() ? 'Redirecting…' : 'Continue with Zoho' }}
          </button>
        }

        @if (auth.authenticationMode() === 'hybrid') {
          <p class="vm-login__divider">or use a temporary local account</p>
        }

        @if (auth.authenticationMode() !== 'zoho') {
          <label class="vm-login__label" for="username">Username</label>
          <input id="username" class="vm-login__input" [(ngModel)]="username" name="username" autocomplete="username">

          <label class="vm-login__label" for="password">Password</label>
          <input id="password" class="vm-login__input" [(ngModel)]="password" name="password" type="password" autocomplete="current-password">

          <button class="vm-login__submit" type="submit" [disabled]="busy()">{{ busy() ? 'Signing in…' : 'Sign in' }}</button>
        }

        @if (error()) {
          <div class="vm-login__error" role="alert">{{ error() }}</div>
        }
      </form>
    </section>
  `,
  styles: `
    .vm-login{min-height:100vh;padding:3rem 1.5rem;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:1.5rem;background:#fff}
    .vm-login__logo-lockup img{height:92px;width:auto}
    .vm-login__form{width:min(360px,100%);display:flex;flex-direction:column;gap:.35rem}
    h1{margin:0;font-size:1.35rem;color:#203452}
    .vm-login__product{margin:0 0 .8rem;color:#2c3f65;font-size:.94rem;font-weight:700;letter-spacing:.02em}
    .vm-login__copy{margin:0 0 .8rem;color:#42546f;line-height:1.45}.vm-login__divider{margin:.65rem 0 .15rem;color:#60748f;font-size:.85rem;text-align:center}
    .vm-login__label{font-weight:700;color:#2c3f65;letter-spacing:.03em}
    .vm-login__input{border:1px solid #9d9d9d;border-radius:8px;padding:.8rem;font-size:1rem;color:#2e3d56;background:#f8f8f8}
    .vm-login__submit{margin-top:1.1rem;width:100%;min-height:42px;padding:.85rem;background:#0071bc;color:#fff;border-radius:8px;font-weight:700;border:0;font-size:1rem;cursor:pointer}
    .vm-login__submit:disabled{opacity:.7;cursor:not-allowed}
    .vm-login__error{margin-top:.5rem;color:#8d1d18;background:#fff0f0;border-left:4px solid #c0392b;border-radius:8px;padding:8px 10px}
  `,
})
export class LoginComponent implements OnInit {
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  username = '';
  password = '';
  busy = signal(false);
  error = signal('');

  async ngOnInit(): Promise<void> {
    if (!this.auth.ready()) await this.auth.loadMe();
    if (this.auth.user()) {
      await this.router.navigateByUrl('/reservations');
      return;
    }
    this.error.set(this.zohoErrorMessage(this.route.snapshot.queryParamMap.get('zoho')));
    if (this.route.snapshot.queryParamMap.get('auth') === 'mode_changed') {
      this.error.set('Application sign-in settings changed. Please sign in again.');
    }
  }

  beginZohoLogin(): void {
    this.error.set('');
    this.busy.set(true);
    this.auth.beginZohoLogin();
  }

  async submit(): Promise<void> {
    if (this.auth.authenticationMode() === 'zoho') return;
    this.error.set('');
    this.busy.set(true);
    try {
      await this.auth.login(this.username, this.password);
    } catch {
      this.error.set('Invalid username or password.');
    } finally {
      this.busy.set(false);
    }
  }

  private zohoErrorMessage(code: string | null): string {
    if (code === 'cancelled') return 'Zoho sign-in was cancelled.';
    if (code === 'link_required') return 'Your Zoho account is not linked to an active importer user. Contact an administrator.';
    if (code === 'state_invalid') return 'Your Zoho sign-in session expired. Please try again.';
    if (code === 'configuration') return 'Zoho sign-in has not been configured yet.';
    if (code === 'sign_in_failed') return 'Unable to sign in with Zoho. Please try again or contact an administrator.';
    return '';
  }
}
